/**
 * scoringView.ts — pure presentation helpers for the scoring console panel (#5).
 *
 * Display-only: maps a raw-f0 `FrameVerdict` / `SessionResult` onto Korean labels,
 * status-tone classes and safe number strings. No React/native dependency, never
 * throws — native frames may carry null/NaN, so every formatter degrades to '—'.
 * Scoring itself stays in the pure `@muzly/shared` core; this only renders it.
 */

import type { FrameVerdict } from '@muzly/shared';

import { STATUS_TONE } from './statusTone';

/** KO 평이체 한 줄 라벨(목표 음 대비 위/아래/정확/무음/놓침/불확실). */
export const VERDICT_LABEL: Record<FrameVerdict, string> = {
  in_tune: '정확',
  high: '높음 ▲',
  low: '낮음 ▼',
  silent: '쉼',
  missed: '놓침',
  unknown: '불확실',
};

/** 판정별 상태색 클래스 — in_tune=정상, high/low=경고, missed=위험, 나머지=중립. */
export const verdictToneClass = (verdict: FrameVerdict): string => {
  switch (verdict) {
    case 'in_tune':
      return STATUS_TONE.ok;
    case 'high':
    case 'low':
      return STATUS_TONE.warn;
    case 'missed':
      return STATUS_TONE.danger;
    default:
      return 'border-border/60 bg-muted/40 text-muted-foreground';
  }
};

/** 부호 포함 cents 문자열(+12¢ / −30¢). null/비유한이면 '—'. */
export const formatCents = (cents: number | null | undefined): string =>
  typeof cents === 'number' && Number.isFinite(cents)
    ? `${cents > 0 ? '+' : ''}${cents.toFixed(0)}¢`
    : '—';

/** 정확도 퍼센트 정수 표기. 비유한이면 '—'. */
export const formatPercent = (pct: number | null | undefined): string =>
  typeof pct === 'number' && Number.isFinite(pct) ? `${pct.toFixed(0)}%` : '—';

const NOTE_NAMES = [
  'C',
  'C#',
  'D',
  'D#',
  'E',
  'F',
  'F#',
  'G',
  'G#',
  'A',
  'A#',
  'B',
];

/** MIDI 번호 → 음이름(C4 등). null/비유한이면 '—'. */
export const midiToNoteName = (midi: number | null | undefined): string => {
  if (typeof midi !== 'number' || !Number.isFinite(midi)) {
    return '—';
  }
  const m = Math.round(midi);
  const name = NOTE_NAMES[((m % 12) + 12) % 12];
  return `${name}${Math.floor(m / 12) - 1}`;
};
